<template>
    <div>
        <transition name="fade" mode="out-in">
            <AppPlaceholder v-if="!user" key="no-user"/>
            <div v-else key="user">
                <div class="row">
                    <div class="col-12 mb-4 d-sm-flex justify-content-between align-items-baseline">
                        <div class="pl-3">
                            <h4>
                                {{ isNew ? `New ${groupCode}` : user.name }}
                            </h4>
                            <p class="mb-0 text-muted" v-if="!isNew">
                                <small>Created on {{ user.created_at | date }}</small>
                            </p>
                        </div>
                        <div class="col-12 col-sm-auto text-right pr-0 mt-2 mt-sm-0">
                            <button class="btn btn-primary mr-2"
                                    :disabled="loading"
                                    @click="save">
                                Save
                            </button>
                            <button class="btn btn-outline-secondary"
                                    :disabled="loading"
                                    @click="saveAndClose">
                                Save & close
                            </button>
                            <b-dropdown variant="link"
                                        no-caret
                                        right
                                        v-if="!isNew">
                                <template v-slot:button-content>
                                    <i class="material-icons">more_vert</i>
                                </template>
                                <b-dropdown-item :disabled="loading"
                                                 @click="destroy">
                                    Delete
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <p class="col-12 text-muted mb-3"><strong>General Info</strong></p>
                        </div>
                        <div class="row">
                            <AppInput
                                    label="First name"
                                    :value="user.first_name"
                                    field="first_name"
                                    :errors="errors"
                                    @change="updateUserProps({ first_name: $event })"
                                    class="col-lg-6"/>
                            <AppInput label="Last name"
                                      :value="user.last_name"
                                      field="last_name"
                                      :errors="errors"
                                      @change="updateUserProps({ last_name: $event })"
                                      class="col-lg-6"/>
                        </div>
                        <div class="row">
                            <AppSelect :value="user.company || null"
                                       field="company_id"
                                       :errors="errors"
                                       label="Company"
                                       :options="companySuggestions"
                                       label-field="name"
                                       :allow-empty="true"
                                       placeholder=""
                                       :disabled="isUserModalOpen"
                                       class="col-lg-5"
                                       @search-change="suggestCompanies($event)"
                                       @input="updateUserProps({
                                           company_id: $event ? $event.id : null,
                                           company: $event})"/>
                            <div class="col-lg-3" v-if="isAdmin">
                                <label>Status</label>
                                <AppCheckbox :errors="errors"
                                             label="Activated"
                                             :value="user.is_activated"
                                             @input="updateUserProps({is_activated: $event})"
                                             field="is_activated"/>
                            </div>
                        </div>
                        <div class="row">
                            <AppInput label="E-mail"
                                      :value="user.email"
                                      field="email"
                                      :errors="errors"
                                      @change="updateUserProps({ email: $event })"
                                      class="col-lg-6"/>
                        </div>
                        <div class="row">
                            <AppInput label="Phone (mobile)"
                                      :value="user.phone_mobile"
                                      field="phone_mobile"
                                      :errors="errors"
                                      @change="updateUserProps({ phone_mobile: $event })"
                                      class="col-lg-6"/>
                            <AppInput label="Phone (office)"
                                      :value="user.phone_office"
                                      field="phone_office"
                                      :errors="errors"
                                      @change="updateUserProps({ phone_office: $event })"
                                      class="col-lg-6"/>
                        </div>
                        <div class="row" v-if="isAdmin">
                            <div class="col-lg-6">
                                <AppInput label="Password"
                                          :value="user.password"
                                          field="password"
                                          autocomplete="new-password"
                                          type="password"
                                          :errors="errors"
                                          @change="updateUserProps({ password: $event })"
                                          class="mb-1"/>
                                <button class="btn btn-sm btn-link"
                                        :disabled="loading"
                                        @click="generatePassword"
                                        v-if="!isNew">Send new password
                                </button>
                            </div>
                            <AppInput label="Confirm password"
                                      :value="user.password_confirmation"
                                      field="password_confirmation"
                                      type="password"
                                      :errors="errors"
                                      @change="updateUserProps({
                                          password_confirmation: $event })"
                                      class="col-lg-6"/>
                        </div>
                        <div class="row">
                            <AppTextarea :value="user.notes"
                                         @input="updateUserProps({ notes: $event })"
                                         label="Comment"
                                         field="notes"
                                         :errors="errors"
                                         class="col-12"/>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import debounce from 'lodash.debounce';
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import NotificationService from '../../services/notification.service';
import AppInput from '../form/AppInput';
import AppPlaceholder from '../form/AppPlaceholder';
import Errors from '../../utils/errors';
import { formatDate } from '../../filters/date.filter';
import AppTextarea from '../form/AppTextarea';
import AppSelect from '../form/AppSelect';
import AppCheckbox from '../form/AppCheckbox';

export default {
  props: ['groupCode'],
  components: {
    AppInput,
    AppPlaceholder,
    AppTextarea,
    AppSelect,
    AppCheckbox,
    BDropdown,
    BDropdownItem,
  },
  name: 'user',
  data() {
    return {
      errors: new Errors(),
      companySuggestions: [],
      loading: false,
      canClose: false,
    };
  },
  filters: {
    date: formatDate,
  },
  computed: {
    ...mapState({
      isNewUser: (state) => state.users.isNewUser,
      isUserModalOpen: (state) => state.users.isUserModalOpen,
    }),
    ...mapGetters({
      user: 'users/user',
      usersGroups: 'users/usersGroups',
      company: 'companies/company',
    }),
    isNew() {
      return this.$route.params.id === 'new' || this.isNewUser;
    },
    isAdmin() {
      return this.groupCode === 'admin';
    },
  },
  mounted() {
    this.initUser();
  },
  watch: {
    '$route.params.id'() {
      this.initUser();
    },
  },
  methods: {
    generatePassword() {
      this.$bvModal.msgBoxConfirm('Automatically generated, new password will be sent to this user\'s email.',
        {
          title: 'Send new password?',
          okTitle: 'Send',
          cancelVariant: 'outline-secondary',
        })
        .then((value) => {
          if (value) {
            this.$store.dispatch('users/generateNewPassword')
              .then((res) => {
                if (res.hasOwnProperty('user_id')) {
                  NotificationService.success(res.message);
                }
              });
          }
        });
    },
    initUser() {
      if (this.isNew) {
        this.$store.dispatch('users/newUser', this.company ? this.company.id : null);
      } else {
        this.getUser();
      }
    },
    suggestCompanies: debounce(function suggest(query) {
      const params = {
        query,
      };
      return this.$store.dispatch('companies/suggestCompanies', params)
        .then((suggestions) => {
          this.companySuggestions = suggestions;
        });
    }, 300),
    getUser() {
      this.$store.dispatch('users/getUser', this.isUserModalOpen && this.user ? this.user.id : this.$route.params.id);
    },
    updateUserProps(props) {
      this.$store.dispatch('users/updateUserProps', props);
    },
    saveAndClose() {
      this.save()
        .then(() => {
          this.$store.commit('users/userId', null);
          if (this.isUserModalOpen) {
            this.$store.commit('users/isUserModalOpen', false);
          } else if (this.canClose) {
            this.$router.push({
              name: 'users',
              params: { groupCode: this.groupCode },
            });
          }
        });
    },
    save() {
      this.loading = true;
      this.errors.clear();
      return this.$store.dispatch(this.isNew ? 'users/createUser' : 'users/updateUser', this.groupCode)
        .then((res) => {
          this.canClose = true;
          if (this.isNew) {
            if (!this.isUserModalOpen) {
              this.$router.push({
                name: 'user',
                params: {
                  groupCode: this.groupCode,
                  id: res.user_id,
                },
              });
            }
          }
          NotificationService.success(res.message);
        })
        .catch((err) => {
          this.canClose = false;
          this.errors.set(err.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    destroy() {
      this.$bvModal.msgBoxConfirm(`Delete user ${this.user.name}?`, {
        okTitle: 'Delete',
        okVariant: 'danger',
      })
        .then((value) => {
          if (value) {
            this.$store.dispatch('users/deleteUser')
              .then((res) => {
                NotificationService.success(res.message);
                this.$store.commit('users/isUserModalOpen', false);
                if (!this.company) {
                  this.$router.push({ name: 'users' });
                }
              });
          }
        });
    },
  },
};
</script>
